import { Flex, Tag } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { ColumnsType } from "antd/es/table";
import { t } from "i18next";
import { ColorText } from "../../components/ColorText/ColorText";
import {
  formatDateDDMMYY,
  formatDateFilterApi,
  formatDateFilterView,
} from "../../utils/dateFormatter";
import { FilterItem, FilterItemType } from "../filters/filters.types";
import {
  MfinExciseLicenceRequest,
  MfinExciseLicenceRequestListPayload,
  MfinExciseStampRequest,
  MfinExciseStampRequestListPayload,
  MfinExciseWarehouse,
  MfinExciseWarehouseListPayload,
} from "./ministry.types";

export const MFIN_EXCISE_STAMP_INITIAL_FILTERS = {
  brojZahteva: null,
  brojMarkice: null,
  datum: null,
  vrstaProizvoda: null,
  statusZahteva: null,
  pib: null,
  nazivPodnosioca: null,
};

export const MFIN_EXCISE_LICENCE_INITIAL_FILTERS = {
  statusZahteva: null,
  tipZahteva: null,
  datum: null,
  vrstaProizvoda: null,
  brojResenja: null,
  datumVazenja: null,
  podnosilacZahteva: null,
  pib: null,
};

export const MFIN_EXCISE_WAREHOUSE_INITIAL_FILTERS = {
  nazivObveznika: null,
  tipResenjaId: null,
};

export const getMfinExciseStampRequestFilterItems = ({
  isLoading,
  isDisabled,
  statuses,
  productTypes,
}: {
  isLoading?: boolean;
  isDisabled?: boolean;
  statuses?: Array<DefaultOptionType>;
  productTypes?: Array<DefaultOptionType>;
}): Array<FilterItem<MfinExciseStampRequestListPayload>> => {
  return [
    {
      fieldName: "brojZahteva",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TABLES.BROJ_ZAHTEVA"),
        disabled: isDisabled,
      },
    },
    {
      fieldName: "vrstaProizvoda",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("TABLES.VRSTA_PROIZVODA"),
        loading: isLoading,
        options: productTypes,
        disabled: isDisabled,
      },
    },
    {
      fieldName: "brojMarkice",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.BROJ_MARKICE"),
        disabled: isDisabled,
      },
    },
    {
      fieldName: "statusZahteva",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("PLACEHOLDERS.STATUS"),
        loading: isLoading,
        options: statuses,
        disabled: isDisabled,
      },
    },
    {
      fieldName: "datum",
      type: FilterItemType.DATE_RANGE,
      props: {
        placeholder: [t("PLACEHOLDERS.DATUM_OD"), t("PLACEHOLDERS.DATUM_DO")],
        disabled: isDisabled,
      },
    },
    {
      fieldName: "nazivPodnosioca",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("COMMON.NAZIV_PODNOSIOCA"),
      },
    },
    {
      fieldName: "pib",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.PIB"),
      },
    },
  ];
};

export const getMfinExciseStampRequestTableColumns = () => {
  return [
    {
      title: t("TABLES.BROJ_ZAHTEVA"),
      dataIndex: "brojZahteva",
      width: "120px",
    },
    { title: t("TABLES.OBVEZNIK"), dataIndex: "obveznik" },
    {
      title: t("TABLES.UPLATA_PREDRACUNA"),
      dataIndex: "uplataPredracuna",
      width: "120px",
      render: (status: boolean) => {
        return status ? t("TABLES.DA") : t("TABLES.NE");
      },
    },
    {
      title: t("TABLES.OPSEG"),
      dataIndex: "opsegIsporucenihMarkica",
      render: (opsezi: string[]) => (
        <Flex gap={4} align="start" vertical>
          {opsezi.map((opseg: string, index: number) => (
            <Tag key={index}>{opseg}</Tag>
          ))}
        </Flex>
      ),
    },
    {
      title: t("TABLES.DATUM"),
      dataIndex: "datum",
    },
    {
      title: t("TABLES.STATUS"),
      dataIndex: "status",
      render: (status: string) => ColorText(status),
    },
  ] as ColumnsType<MfinExciseStampRequest>;
};

export const getMfinExciseLicenceRequestFilterItems = ({
  isLoading,
  isDisabled,
  statuses,
  productTypes,
  requestTypes,
}: {
  isLoading?: boolean;
  isDisabled?: boolean;
  statuses?: Array<DefaultOptionType>;
  productTypes?: Array<DefaultOptionType>;
  requestTypes?: Array<DefaultOptionType>;
}): Array<FilterItem<MfinExciseLicenceRequestListPayload>> => {
  return [
    {
      fieldName: "statusZahteva",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("PLACEHOLDERS.STATUS"),
        loading: isLoading,
        options: statuses,
        disabled: isDisabled,
      },
    },
    {
      fieldName: "tipZahteva",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("TABLES.TIP_ZAHTEVA"),
        loading: isLoading,
        options: requestTypes,
      },
    },
    {
      fieldName: "datum",
      type: FilterItemType.DATE_RANGE,
      props: {
        placeholder: [t("PLACEHOLDERS.DATUM_OD"), t("PLACEHOLDERS.DATUM_DO")],
      },
    },
    {
      fieldName: "vrstaProizvoda",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("TABLES.VRSTA_PROIZVODA"),
        loading: isLoading,
        options: productTypes,
      },
    },
    {
      fieldName: "brojResenja",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TABLES.BROJ_RESENJA"),
      },
    },
    {
      fieldName: "datumVazenja",
      type: FilterItemType.DATE,
      props: {
        placeholder: t("PLACEHOLDERS.DATUM_VAZENJA"),
      },
      formatForApi: formatDateFilterApi,
      formatForClient: formatDateFilterView,
    },
    {
      fieldName: "podnosilacZahteva",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.PODNOSILAC_ZAHTEVA"),
      },
    },
    {
      fieldName: "pib",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.PIB"),
      },
    },
  ];
};

export const getMfinExciseLicenceRequestTableColumns = () => {
  return [
    {
      title: t("TABLES.BROJ_ZAHTEVA"),
      dataIndex: "brojZahteva",
      width: "120px",
    },
    {
      title: t("TABLES.DATUM_ZAHTEVA"),
      dataIndex: "datumZahteva",
      render: (datum: string) => formatDateDDMMYY(datum),
    },
    { title: t("TABLES.TIP_ZAHTEVA"), dataIndex: "tipZahteva" },
    { title: t("TABLES.PODNOSILAC_ZAHTEVA"), dataIndex: "podnosilacZahteva" },
    { title: t("TABLES.BROJ_RESENJA"), dataIndex: "brojResenja" },
    {
      title: t("TABLES.DATUM_DO_KADA_VAZI_RESENJE"),
      dataIndex: "datumVazenja",
      render: (datum: string) => formatDateDDMMYY(datum),
    },
    {
      title: t("TABLES.STATUS"),
      dataIndex: "statusZahteva",
      render: (status: string) => ColorText(status),
    },
  ] as ColumnsType<MfinExciseLicenceRequest>;
};

export const getMfinExciseWarehouseFilterItems = ({
  requestTypes,
  isLoading,
  isDisabled,
}: {
  requestTypes?: Array<DefaultOptionType>;
  isLoading?: boolean;
  isDisabled?: boolean;
}): Array<FilterItem<MfinExciseWarehouseListPayload>> => {
  return [
    {
      fieldName: "nazivObveznika",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.NAZIV_OBVEZNIKA"),
      },
    },
    {
      fieldName: "tipResenjaId",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("PLACEHOLDERS.TIP_RESENJA"),
        options: requestTypes,
        loading: isLoading,
        disabled: isDisabled,
      },
    },
  ];
};

export const getMfinExciseWarehouseTableColumns = () => {
  return [
    { title: t("TABLES.OZNAKA_SKLADISTA"), dataIndex: "oznakaSkladista" },
    { title: t("TABLES.NAZIV_OBVEZNIKA"), dataIndex: "nazivObveznika" },
    {
      title: t("TABLES.BROJ_VAZECEG_RESENJA"),
      dataIndex: "brojVazecegResenja",
    },
    { title: t("TABLES.TIP_RESENJA"), dataIndex: "tipResenja" },
    {
      title: t("TABLES.DATUM_DONOSENJA_RESENJA"),
      dataIndex: "datumDonosenjaResenja",
    },
    {
      title: t("TABLES.DATUM_DO_KADA_VAZI_RESENJE"),
      dataIndex: "datumVazenjaResenja",
    },
  ] as ColumnsType<MfinExciseWarehouse>;
};
